import { addPerfObserverCallback } from 'owa-performance';
import { logUsage } from '../api/logUsage';

type LongRunningTask = {
    key: number;
    start: number;
    end: number;
};

type Datapoint = {
    name: string;
    start: number;
};

export const LONG_RUNNING_TASK_THRESHOLD = 100;

let performanceObserver: PerformanceObserver | undefined = undefined;
let longRunningTasksCount: LongRunningTask[] = [];
let datapoints: Map<string, Datapoint> = new Map();
let partialOverlaps: Map<number, Set<string>> = new Map();

export function trackLongRunningTask(datapointName: string, start: number) {
    const key = getKey(datapointName, start);
    datapoints.set(key, { name: datapointName, start });

    initializePerfObserver();
}

export function endLongRunningTaskTracking(
    datapointName: string,
    start: number,
    end: number
): number {
    let count: number = 0;
    const key = getKey(datapointName, start);

    for (const task of longRunningTasksCount) {
        if (task.start >= start && task.end <= end) {
            count++;
        } else if (
            // The task only partially overlaps this datapoint
            (task.end >= start && task.end <= end) ||
            (task.start >= start && task.start <= task.end) ||
            (task.start < start && task.end > end)
        ) {
            let datapointsSet = partialOverlaps.get(task.key);
            if (!datapointsSet) {
                datapointsSet = new Set();
                partialOverlaps.set(task.key, datapointsSet);
            }
            datapointsSet.add(datapointName);
        }
    }

    datapoints.delete(key);
    cleanUpTasks();

    return count;
}

export function getAllLongRunningTasks(): LongRunningTask[] {
    return longRunningTasksCount;
}

function initializePerfObserver() {
    const entryType = 'longtask';

    if (!performanceObserver) {
        performanceObserver = addPerfObserverCallback(entryType, (list: PerformanceEntryList) => {
            for (const entry of <PerformanceLongTaskTiming[]>list) {
                const duration = Math.round(entry.duration);
                if (duration > LONG_RUNNING_TASK_THRESHOLD) {
                    longRunningTasksCount.push({
                        key: Date.now(),
                        start: entry.startTime,
                        end: entry.startTime + duration,
                    });
                }
            }
        });
    }
}

const getKey = (name: string, start: number): string => `${name}_${start}`;

const cleanUpTasks = () => {
    const datapointsArray = Array.from(datapoints.values());

    longRunningTasksCount = longRunningTasksCount.filter(task => {
        const canBeDeleted = datapointsArray.every(d => d.start > task.end);
        const hasPartialOverlap = partialOverlaps.has(task.key);
        if (canBeDeleted && hasPartialOverlap) {
            logAndDeleteOverlap(task);
        }

        return !canBeDeleted && hasPartialOverlap;
    });
};

const logAndDeleteOverlap = (task: LongRunningTask) => {
    const dpList = Array.from(partialOverlaps?.get(task.key)?.values() || []);
    logUsage('OverlappingLongRunningTask', {
        datapoints: dpList.toString(),
    });
    partialOverlaps.delete(task.key);
};
